import * as React from "react";
import { ReactSortable } from "react-sortablejs";
import { Info, Loader } from "lucide-react";
import { ICompassMessage, ICompasAIChatAction } from "@/types/compass.types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { BulkActionButtons } from "./ActionItemsToolbar";
import { ActionItemRow } from "./ActionItemsList";
import { useActionItemsHandlers } from "./action-items.utils";
import { getBackendDocumentId, notifyDocumentNotReady } from "@/utils/document-id.utils";
const OneClickAutomationDialog = React.lazy(
  () => import("@/taskpane/components/OneClickAutomationDialog")
);

interface ActionItemsProps {
  message: ICompassMessage;
  messages: ICompassMessage[];
}

const ActionItemsHeader: React.FC<{
  message: ICompassMessage;
  isFullyExpanded: boolean;
  toggleExpandAll: () => void;
  handleEditAll: () => void;
  handleBulkActionToggle: (checked: boolean) => void;
  onOneClickAutomation: () => void;
  actionsLength: number;
  isBulkActionChecked: boolean;
  isGlobalEditMode: boolean;
}> = ({
  message,
  isFullyExpanded,
  toggleExpandAll,
  handleEditAll,
  handleBulkActionToggle,
  onOneClickAutomation,
  actionsLength,
  isBulkActionChecked,
  isGlobalEditMode,
}) => (
  <div className="flex justify-between items-center mb-3">
    <h5 className="flex items-center gap-2 font-medium">
      Action Items
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Info className="w-3 h-3 cursor-help" />
          </TooltipTrigger>
          <TooltipContent>
            <p>Actions that might interest you</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </h5>
    <div className="flex items-center gap-2">
      <BulkActionButtons
        message={message}
        isExpanded={isFullyExpanded}
        toggleExpandAll={toggleExpandAll}
        onEditAll={handleEditAll}
        onBulkActionToggle={handleBulkActionToggle}
        onOneClickAutomation={onOneClickAutomation}
        isAnyAction={actionsLength > 0}
        isBulkActionChecked={isBulkActionChecked}
        isGlobalEditMode={isGlobalEditMode}
      />
    </div>
  </div>
);

const SortableActionItemsList: React.FC<{
  actions: ICompasAIChatAction[];
  handleReorder: (newList: ICompasAIChatAction[]) => void;
  expandedItems: Set<string>;
  toggleExpansion: (actionId: string) => void;
  isGlobalEditMode: boolean;
  moveAction: (from: number, to: number) => void;
}> = ({ actions, handleReorder, expandedItems, toggleExpansion, isGlobalEditMode, moveAction }) => (
  <React.Suspense
    fallback={
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <Loader className="animate-spin" size={48} />
        <p>Loading...</p>
      </div>
    }
  >
    <ReactSortable list={actions} setList={handleReorder} className="flex flex-col gap-2">
      {actions.map((action, index) => (
        <ActionItemRow
          key={action.id}
          action={action}
          index={index}
          actionsLength={actions.length}
          isExpanded={expandedItems.has(action.id)}
          toggleExpansion={() => toggleExpansion(action.id)}
          isGlobalEditMode={isGlobalEditMode}
          moveAction={moveAction}
        />
      ))}
    </ReactSortable>
  </React.Suspense>
);

const OneClickAutomationDialogSection: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  actions: ICompasAIChatAction[];
  docId?: string;
}> = ({ isOpen, onClose, actions, docId }) => (
  <React.Suspense fallback={null}>
    {isOpen && docId != null && (
      <OneClickAutomationDialog
        isOpen={isOpen}
        onClose={onClose}
        actions={actions}
        documentId={docId}
      />
    )}
  </React.Suspense>
);

const useActionItemsState = (message: ICompassMessage) => {
  const [expandedItems, setExpandedItems] = React.useState<Set<string>>(new Set());
  const [, setEditAllEvent] = React.useState(0);
  const [isBulkActionChecked, setIsBulkActionChecked] = React.useState(false);
  const [isGlobalEditMode, setIsGlobalEditMode] = React.useState(false);
  const [isOneClickDialogOpen, setIsOneClickDialogOpen] = React.useState(false);
  const [actions, setActions] = React.useState(message.actions ?? []);

  React.useEffect(() => {
    setActions(message.actions ?? []);
  }, [message.actions]);

  return {
    expandedItems,
    setExpandedItems,
    setEditAllEvent,
    isBulkActionChecked,
    setIsBulkActionChecked,
    isGlobalEditMode,
    setIsGlobalEditMode,
    isOneClickDialogOpen,
    setIsOneClickDialogOpen,
    actions,
    setActions,
  };
};

const getToggleExpansion = (
  expandedItems: Set<string>,
  setExpandedItems: React.Dispatch<React.SetStateAction<Set<string>>>,
  actions: ICompassMessage["actions"]
) => {
  const toggleExpandAll = () => {
    if (expandedItems.size === (actions?.length ?? 0)) {
      setExpandedItems(new Set());
    } else {
      setExpandedItems(new Set((actions ?? []).map((a) => a.id)));
    }
  };
  return { toggleExpandAll };
};

const createToggleExpansion =
  (setExpandedItems: React.Dispatch<React.SetStateAction<Set<string>>>) => (actionId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(actionId)) {
        newSet.delete(actionId);
      } else {
        newSet.add(actionId);
      }
      return newSet;
    });
  };

const ActionItems: React.FC<ActionItemsProps> = ({ message }) => {
  const {
    expandedItems,
    setExpandedItems,
    setEditAllEvent,
    isBulkActionChecked,
    setIsBulkActionChecked,
    isGlobalEditMode,
    setIsGlobalEditMode,
    isOneClickDialogOpen,
    setIsOneClickDialogOpen,
    actions,
    setActions,
  } = useActionItemsState(message);

  const { doc, handleReorder, handleRunAllActions, handleCancelAllPendingActions, moveAction } =
    useActionItemsHandlers({ message, actions, setActions, setIsBulkActionChecked });

  const toggleExpansion = createToggleExpansion(setExpandedItems);
  const { toggleExpandAll } = getToggleExpansion(expandedItems, setExpandedItems, actions);

  const handleEditAll = () => {
    setIsGlobalEditMode((prev) => !prev);
    setEditAllEvent(Date.now());
  };

  const handleBulkActionToggle = (checked: boolean) => {
    setIsBulkActionChecked(checked);
    if (checked) {
      handleRunAllActions();
    } else {
      handleCancelAllPendingActions();
    }
  };

  const isFullyExpanded = expandedItems.size === actions.length && actions.length > 0;

  return (
    <div className="w-full mt-2">
      <ActionItemsHeader
        message={message}
        isFullyExpanded={isFullyExpanded}
        toggleExpandAll={toggleExpandAll}
        handleEditAll={handleEditAll}
        handleBulkActionToggle={handleBulkActionToggle}
        onOneClickAutomation={() => {
          if (getBackendDocumentId(doc) == null) {
            notifyDocumentNotReady("compass");
            return;
          }
          setIsOneClickDialogOpen(true);
        }}
        actionsLength={actions.length}
        isBulkActionChecked={isBulkActionChecked}
        isGlobalEditMode={isGlobalEditMode}
      />
      <OneClickAutomationDialogSection
        isOpen={isOneClickDialogOpen}
        onClose={() => setIsOneClickDialogOpen(false)}
        actions={actions}
        docId={getBackendDocumentId(doc)}
      />
      <SortableActionItemsList
        actions={actions}
        handleReorder={handleReorder}
        expandedItems={expandedItems}
        toggleExpansion={toggleExpansion}
        isGlobalEditMode={isGlobalEditMode}
        moveAction={moveAction}
      />
    </div>
  );
};

export default ActionItems;
